/**
 * The shared file viewer's front door.
 *
 * Any surface — a page, a widget, or a component sitting inside another modal —
 * opens the viewer with one call and no markup of its own:
 *
 *     openFileViewer(files.map((f, i) => toViewable.message(f, msg.id, i)), { index: 2 });
 *
 * The viewer itself is a single `<ion-modal>` in `FileViewerHost.vue`, mounted
 * once in App.vue (LAST, after the other hosts — Ionic's hardware-back handling
 * takes the last overlay in document order). A Host mounted directly under
 * `<ion-app>` always paints above a modal opened by a page, because
 * `ion-router-outlet` is its own `z-index: 0` stacking context, which is what
 * lets the three call sites that are themselves modals open the viewer safely.
 *
 * This module is the module-level reactive singleton that Host reads — the
 * same shape as `src/pwa/offline-ui.ts` — so there is exactly one viewer and no
 * page has to own one.
 */
import { shallowReadonly, shallowRef, type ShallowRef } from 'vue';
import type { OpenFileViewerOptions, ViewableFile } from '@/types/viewable-file';

export interface FileViewerRequest {
  files: ViewableFile[];
  index: number;
  resolve?: OpenFileViewerOptions['resolve'];
  /**
   * Monotonic per-open token. The viewer captures it before any `await` and
   * discards results that resolve after a newer open or a close — the
   * session-epoch guard from `erpat-client.ts`, applied to a PDF fetch that
   * outlives its modal.
   */
  generation: number;
}

const request = shallowRef<FileViewerRequest | null>(null);
let generation = 0;

/** The pending request, or null when the viewer is closed. Read by the Host. */
export const fileViewerRequest: Readonly<ShallowRef<FileViewerRequest | null>> = shallowReadonly(request);

/**
 * Open the viewer on `files`, starting at `opts.index`.
 *
 * Files with nothing to show (no URL) are dropped up front so the swiper never
 * lands on a blank slide; if nothing is left the call is a no-op.
 */
export function openFileViewer(files: ViewableFile[], opts: OpenFileViewerOptions = {}): void {
  const usable = files.filter((f) => f.url);
  if (usable.length === 0) return;

  const wanted = files[opts.index ?? 0];
  // Re-find the requested file after the filter so the index still points at it.
  const index = wanted ? Math.max(0, usable.indexOf(wanted)) : 0;

  generation += 1;
  request.value = { files: usable, index, resolve: opts.resolve, generation };
}

/** Close the viewer (idempotent). The Host also calls this on `@did-dismiss`. */
export function closeFileViewer(): void {
  if (request.value === null) return;
  generation += 1;
  request.value = null;
}

/** Whether `gen` is still the live open — false once the viewer closed or reopened. */
export function isCurrentGeneration(gen: number): boolean {
  return request.value !== null && request.value.generation === gen;
}
